/**
 * Componente Principal - Módulo de Compras Rediseñado
 * Gestiona el flujo completo de creación de compras
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import axios from 'axios';
import { useCompras } from './hooks/useCompras';
import { useProductos } from './hooks/useProductos';
import { useRetenciones } from './hooks/useRetenciones';
import { useCuentas } from './hooks/useCuentas';
import SubidaXML from './components/SubidaXML';
import EdicionDatos from './components/EdicionDatos';
import EdicionItems from './components/EdicionItems';
import SeleccionCuenta from './components/SeleccionCuenta';
import ConfiguracionRetenciones from './components/ConfiguracionRetenciones';
import NotaObservacion from './components/NotaObservacion';
import ResumenCompra from './components/ResumenCompra';
import ListaCompras from './components/ListaCompras';

const steps = [
  'Subida XML',
  'Datos Básicos',
  'Items',
  'Cuentas',
  'Retenciones',
  'Resumen',
];

const ComprasDinamicoNuevo = () => {
  // Obtener empresaId del usuario logueado
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const empresaId = user.empresaId || '';
  const [paso, setPaso] = useState(0);
  const [mostrarLista, setMostrarLista] = useState(false);

  // Hooks
  const { compras, loading, error, crearCompra, cargarCompras, limpiarError } =
    useCompras(empresaId);
  const { productos, buscarProductos } = useProductos(empresaId);
  const { codigos, cargarCodigos } = useRetenciones(empresaId);
  const { cuentas, configuracion, cargarPlanCuentas, cargarConfiguracion } =
    useCuentas(empresaId);

  // Bodegas
  const [bodegas, setBodegas] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL || '';
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Estado de la compra
  const [compraData, setCompraData] = useState({
    numero_factura: '',
    fecha_factura: new Date().toISOString().split('T')[0],
    codigo_autorizacion: '',
    fecha_ingreso: new Date().toISOString().split('T')[0],
    nota: '',
    proveedor_id: '',
    proveedor_nombre: '',
    proveedor_ruc: '',
    proveedor_direccion: '',
    proveedor_existente: false,
    items: [],
    cuenta_principal_id: '',
    cuenta_principal_codigo: '',
    cuenta_principal_nombre: '',
    cuenta_proveedores_id: '',
    cuenta_iva_compras_id: '',
    cuenta_deducible_id: '',
    cuenta_inventario_id: '',
    aplicar_retenciones: false,
    retenciones: [],
  });

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      try {
        await cargarPlanCuentas();
      } catch (e) { /* silenciar */ }
      try {
        await cargarConfiguracion();
      } catch (e) { /* silenciar */ }
      try {
        await cargarCodigos();
      } catch (e) { /* silenciar */ }
      try {
        await cargarCompras();
      } catch (e) { /* silenciar */ }
      // Cargar bodegas
      try {
        const res = await axios.get(`${API_URL}/api/bodegas`, { headers: getHeaders() });
        setBodegas(res.data?.data || []);
      } catch (e) { /* silenciar */ }
    };
    cargarDatosIniciales();
  }, []);

  // Actualizar datos de compra
  const actualizarCompraData = (campo, valor) => {
    setCompraData((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  // Actualizar items
  const actualizarItems = (items) => {
    setCompraData((prev) => ({
      ...prev,
      items,
    }));
  };

  // Actualizar retenciones
  const actualizarRetenciones = (retenciones) => {
    setCompraData((prev) => ({
      ...prev,
      retenciones,
    }));
  };

  // Parsear XML
  const handleXMLParsed = (xmlData) => {
    setCompraData((prev) => ({
      ...prev,
      numero_factura: xmlData.numero_factura || '',
      fecha_factura: xmlData.fecha_factura || prev.fecha_factura,
      codigo_autorizacion: xmlData.codigo_autorizacion || '',
      proveedor_nombre: xmlData.proveedor_nombre || '',
      proveedor_ruc: xmlData.proveedor_ruc || '',
      proveedor_direccion: xmlData.proveedor_direccion || '',
      proveedor_id: xmlData.proveedor_ruc || '', // El RUC es el ID
      items: xmlData.items || [],
    }));
    setPaso(1);
  };

  // Guardar compra
  // Estado del resultado después de guardar
  const [compraGuardada, setCompraGuardada] = useState(null);

  const handleGuardarCompra = async () => {
    try {
      const resultado = await crearCompra(compraData);
      setCompraGuardada(resultado);
    } catch (err) {
      console.error('Error guardando compra:', err);
    }
  };

  // Validar paso actual
  const validarPaso = () => {
    switch (paso) {
      case 0:
        return compraData.items.length > 0;
      case 1:
        return !!compraData.fecha_ingreso;
      case 2:
        return compraData.items.length > 0;
      case 3:
        return true; // Siempre permite avanzar desde Cuentas
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  if (compraGuardada) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6">✅ Compra Registrada Exitosamente</Typography>
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">N° Factura</Typography>
              <Typography fontWeight="bold">{compraGuardada.numero_factura}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Proveedor</Typography>
              <Typography fontWeight="bold">{compraGuardada.proveedor_nombre}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Total</Typography>
              <Typography fontWeight="bold" color="primary">${compraGuardada.total_a_pagar || compraGuardada.subtotal}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Estado</Typography>
              <Chip label={compraGuardada.estado || 'REGISTRADA'} color="success" />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Asiento Contable</Typography>
              <Chip label={compraGuardada.asiento_id ? '✓ Generado' : 'Sin asiento'} 
                color={compraGuardada.asiento_id ? 'success' : 'warning'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Retención</Typography>
              <Chip label={compraGuardada.retencion_estado || 'Sin retención'} 
                color={compraGuardada.retencion_estado === 'PENDIENTE_AUTORIZAR' ? 'warning' : 'default'} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Acciones:</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="primary"
              onClick={() => window.print()}>
              📄 Descargar Comprobante de Ingreso
            </Button>

            {compraGuardada.retencion_estado === 'PENDIENTE_AUTORIZAR' && (
              <Button variant="contained" color="warning">
                📋 Autorizar Retención
              </Button>
            )}

            <Button variant="outlined" onClick={() => {
              setCompraGuardada(null);
              setMostrarLista(true);
              cargarCompras();
            }}>
              Ver Lista de Compras
            </Button>

            <Button variant="outlined" color="success" onClick={() => {
              setCompraGuardada(null);
              setPaso(0);
              setCompraData({
                numero_factura: '', fecha_factura: new Date().toISOString().split('T')[0],
                codigo_autorizacion: '', fecha_ingreso: new Date().toISOString().split('T')[0],
                nota: '', proveedor_id: '', proveedor_nombre: '', proveedor_ruc: '',
                proveedor_direccion: '', proveedor_existente: false, items: [],
                cuenta_principal_id: '', cuenta_principal_codigo: '', cuenta_principal_nombre: '',
                cuenta_proveedores_id: '', cuenta_iva_compras_id: '', cuenta_deducible_id: '',
                cuenta_inventario_id: '', aplicar_retenciones: false, retenciones: [],
              });
            }}>
              + Nueva Compra
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (mostrarLista) {
    return (
      <ListaCompras
        compras={compras}
        loading={loading}
        onNuevaCompra={() => {
          setMostrarLista(false);
          setPaso(0);
        }}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <h1>Módulo de Compras Dinámicas</h1>

        {error && (
          <Alert severity="error" onClose={limpiarError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={paso} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            {paso === 0 && (
              <SubidaXML onXMLParsed={handleXMLParsed} loading={loading} />
            )}

            {paso === 1 && (
              <EdicionDatos
                compraData={compraData}
                onActualizar={actualizarCompraData}
              />
            )}

            {paso === 2 && (
              <EdicionItems
                items={compraData.items}
                onItemsChange={actualizarItems}
                productos={productos}
                cuentas={cuentas}
                onBuscarProducto={buscarProductos}
                cuentaDeducible={compraData.cuenta_deducible_id}
                onCuentaDeducibleChange={(valor) => actualizarCompraData('cuenta_deducible_id', valor)}
                cuentaInventario={compraData.cuenta_inventario_id}
                onCuentaInventarioChange={(valor) => actualizarCompraData('cuenta_inventario_id', valor)}
                bodegas={bodegas}
              />
            )}

            {paso === 3 && (
              <SeleccionCuenta
                compraData={compraData}
                cuentas={cuentas}
                configuracion={configuracion}
                onActualizar={actualizarCompraData}
              />
            )}

            {paso === 4 && (
              <ConfiguracionRetenciones
                aplicar={compraData.aplicar_retenciones}
                retenciones={compraData.retenciones}
                onRetencionesCambio={actualizarRetenciones}
                codigosRetenciones={codigos}
                total={
                  parseFloat(compraData.items.reduce((sum, item) => {
                    const subtotal = parseFloat(item.subtotal || 0);
                    const iva = subtotal * 0.15;
                    return sum + subtotal + iva;
                  }, 0)) || 0
                }
                onAplicarCambio={(aplicar) =>
                  actualizarCompraData('aplicar_retenciones', aplicar)
                }
              />
            )}

            {paso === 5 && (
              <ResumenCompra
                compraData={compraData}
                items={compraData.items}
                retenciones={compraData.retenciones}
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                disabled={paso === 0}
                onClick={() => setPaso(paso - 1)}
              >
                Anterior
              </Button>

              {paso < steps.length - 1 ? (
                <Button
                  variant="contained"
                  disabled={!validarPaso()}
                  onClick={() => setPaso(paso + 1)}
                >
                  Siguiente
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  disabled={!validarPaso() || loading}
                  onClick={handleGuardarCompra}
                >
                  {loading ? <CircularProgress size={24} /> : 'Guardar Compra'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ComprasDinamicoNuevo;
